import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Truck, ShieldCheck, RefreshCcw, Package } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { formatUSD } from "@/lib/format";
import { Footer } from "@/components/Footer";
import { ReviewSection } from "@/components/ReviewSection";
import { BackButton } from "@/components/BackButton";

const toCm = (mm) => (mm ? Math.round(mm / 10) : null);

export default function ProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQtyLocal] = useState(1);
  const [variant, setVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setVariant(null);
    setActiveImg(0);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        const v = (r.data.variants || [])[0];
        if (v) setVariant(v);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const imgs = product.images && product.images.length ? product.images : [product.image];
    return imgs.filter(Boolean);
  }, [product]);

  if (notFound) {
    return (
      <main className="flex min-h-screen flex-col items-start justify-center px-6 sm:px-10" data-testid="product-not-found">
        <h1 className="font-serif text-4xl font-light">That piece has wandered off.</h1>
        <Link to="/shop" className="mt-8 text-xs uppercase tracking-[0.2em] text-taupe hover:text-charcoal">
          Back to the shop
        </Link>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="product-loading">
        <p className="text-sm font-light text-ash">Unwrapping...</p>
      </main>
    );
  }

  const price = variant?.price ?? product.price;
  const dims = variant && (toCm(variant.length_mm) || toCm(variant.width_mm) || toCm(variant.height_mm))
    ? `${toCm(variant.length_mm) || "—"} × ${toCm(variant.width_mm) || "—"} × ${toCm(variant.height_mm) || "—"} cm`
    : null;
  const weight = variant?.weight_g ? `${(variant.weight_g / 1000).toFixed(1)} kg` : null;
  const stock = variant?.stock ?? product.stock;
  return (
    <main data-testid="product-detail-page" className="pt-24">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-6 py-16 sm:px-10 lg:grid-cols-12">
        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          <div className="overflow-hidden bg-sand">
            <img
              src={gallery[activeImg]}
              alt={product.name}
              data-testid="product-image"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-2" data-testid="product-gallery">
              {gallery.map((src, i) => (
                <button
                  key={src + i}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`h-20 w-20 shrink-0 overflow-hidden border transition-colors duration-300 ${
                    i === activeImg ? "border-charcoal" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt={`${product.name} view ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex flex-col justify-center lg:col-span-5"
        >
          <BackButton label="Back" fallback="/shop" />
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-taupe">{product.category}</p>
          <h1 data-testid="product-name" className="font-serif text-4xl font-light tracking-tight sm:text-5xl">
            {product.name}
          </h1>
          <p data-testid="product-price" className="mt-5 text-xl text-ash">{formatUSD(price)}</p>
          {product.description && (
            <p data-testid="product-description" className="mt-8 max-w-md text-base font-light leading-relaxed text-ash">
              {product.description}
            </p>
          )}
          {(product.variants || []).length > 1 && (
            <div className="mt-8" data-testid="variant-selector">
              <p className="mb-3 text-xs uppercase tracking-[0.2em] text-ash">Options</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <button
                    key={v.vid}
                    data-testid={`variant-${v.vid}`}
                    onClick={() => setVariant(v)}
                    className={`rounded-full border px-4 py-2 text-xs transition-colors duration-300 ${
                      variant?.vid === v.vid
                        ? "border-charcoal bg-charcoal text-bone"
                        : "border-charcoal/20 text-ash hover:border-charcoal hover:text-charcoal"
                    }`}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mt-10 flex items-center gap-6">
            <div className="flex items-center gap-4 rounded-full border border-charcoal/20 px-4 py-2">
              <button data-testid="qty-minus" onClick={() => setQtyLocal(Math.max(1, qty - 1))} className="p-1 transition-colors duration-300 hover:text-taupe" aria-label="Decrease quantity">
                <Minus size={14} />
              </button>
              <span data-testid="qty-value" className="w-6 text-center text-sm">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQtyLocal(Math.min(20, qty + 1))} className="p-1 transition-colors duration-300 hover:text-taupe" aria-label="Increase quantity">
                <Plus size={14} />
              </button>
            </div>
            <button
              data-testid="add-to-cart-button"
              onClick={() => {
                add(product, qty, variant);
                toast(`${product.name} added to your cart`);
              }}
              className="flex flex-1 items-center justify-center gap-3 rounded-full bg-charcoal px-8 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss"
            >
              <ShoppingBag size={15} /> Add to cart
            </button>
          </div>
          <div className="mt-12 space-y-3 border-t border-black/10 pt-8 text-sm font-light text-ash" data-testid="product-specs">
            {dims && <p className="flex items-center gap-3"><Package size={16} className="text-taupe" /> Dimensions: <span data-testid="spec-dimensions" className="text-charcoal">{dims}</span></p>}
            {weight && <p className="flex items-center gap-3"><Package size={16} className="text-taupe" /> Weight: <span data-testid="spec-weight" className="text-charcoal">{weight}</span></p>}
            {stock > 0 && (
              <p className="flex items-center gap-3">
                <Package size={16} className="text-taupe" />
                In stock — <span data-testid="spec-stock" className="text-charcoal">{stock.toLocaleString()} available</span>
              </p>
            )}
          </div>
          <div className="mt-8 space-y-4 border-t border-black/10 pt-8 text-sm font-light text-ash">
            <p className="flex items-center gap-3"><Truck size={16} className="text-taupe" /> Delivered across New York, by hand — usually within a few days.</p>
            <p className="flex items-center gap-3"><ShieldCheck size={16} className="text-taupe" /> Checked piece by piece before it leaves us.</p>
            <p className="flex items-center gap-3"><RefreshCcw size={16} className="text-taupe" /> Changed your mind? Easy local returns.</p>
          </div>
        </motion.div>
      </div>
      <ReviewSection slug={slug} />
      <Footer />
    </main>
  );
}
